import get from 'lodash.get';
import * as yup from 'yup';
import { Client } from 'store/clients';
import { FONTS, Font } from 'constants/fonts';
import { getSortedList, tryToParseObject } from 'utils/stringFormat';
import { validateText } from 'utils/validation';
import { CustomerForm, CustomerSortKey } from './types';

/** Schema for validating the customer form */
export const customerSchema = yup.object().shape({
  name: validateText()
});

/** Restricted fonts a customer can be given access to, sorted by display name */
export const restrictedFontOptions: Font[] = getSortedList(
  FONTS.filter((font) => font.isRestricted),
  'displayName'
);

/** Parses the restricted fonts form value (a JSON string) into a list of font codes */
export const parseRestrictedFonts = (value: string): string[] => {
  const parsed = tryToParseObject(value);
  return Array.isArray(parsed) ? parsed : [];
};

/**
 * Returns the restricted font codes in dropdown order so the form can detect real changes.
 * Codes not offered in the dropdown are left out.
 */
export const sortRestrictedFonts = (codes: string[]): string[] =>
  restrictedFontOptions.filter((font) => codes.includes(font.code)).map((font) => font.code);

/**
 * Generates the customer form based on the provided client (aka customer).
 * If no client is provided, the fields are set with empty values.
 */
export const getCustomerForm = (client?: Client): CustomerForm => {
  const name = get(client, 'name', '');
  const clientRestrictedFonts = get(client, 'configuration.restrictedFonts', []);
  const restrictedFonts = JSON.stringify(
    Array.isArray(clientRestrictedFonts) ? sortRestrictedFonts(clientRestrictedFonts) : clientRestrictedFonts
  );

  return {
    name: {
      error: null,
      initialValue: name,
      propertyPath: 'name',
      readOnly: false,
      shouldParse: false,
      value: name
    },
    restrictedFonts: {
      error: null,
      initialValue: restrictedFonts,
      propertyPath: 'configuration.restrictedFonts',
      readOnly: false,
      shouldParse: true,
      value: restrictedFonts
    }
  };
};

const compareText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * Returns the client's integration names, de-duplicated and sorted alphabetically (ignoring case).
 * Names are shown exactly as returned by the API.
 */
export const getIntegrationNames = (client: Client): string[] => {
  return [...new Set(client.integrations ?? [])].sort(compareText);
};

/**
 * Sorts clients by the given key and direction, ignoring case.
 * When sorting by integrations, clients without integrations are always listed last
 * and ties are broken by client name.
 */
export const getSortedClients = (
  clients: Client[],
  integrationNames: Record<string, string>,
  sortKey: CustomerSortKey,
  sortDirection: 'asc' | 'desc'
): Client[] => {
  const direction = sortDirection === 'asc' ? 1 : -1;

  return [...clients].sort((a, b) => {
    if (sortKey === 'name') return compareText(a.name, b.name) * direction;

    const aIntegrations = integrationNames[a.id];
    const bIntegrations = integrationNames[b.id];

    if (!aIntegrations !== !bIntegrations) return aIntegrations ? -1 : 1;

    const result = compareText(aIntegrations, bIntegrations);
    return result === 0 ? compareText(a.name, b.name) : result * direction;
  });
};
